import { normalizeMarkdownToTiptap } from '../markdownToTiptap';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError, readServerRequestId } from './common';
import { withServerRequestId } from './error';
import type { CodeReview } from './codeReviews';
import type { CoAction } from './coActions';
import type { CompletionReport } from './completionReports';
import type { Convention } from './conventions';
import type { SkillPackage } from './skills';
import type { Document } from './documents';
import type { HomeActivityEntityType } from './me';
import type { PlanDetailData } from './plans';
import type { PostMortem } from './postMortems';

/**
 * 문서 GraphQL wire payload. 서버는 파생 `bodyTiptap`(원본 body의 약 4.4배)을 싣지 않고
 * 원본 마크다운 `body`만 보내며, 이 모듈이 브라우저에서 변환해 완전한 `Document`로 되돌린다.
 * 따라서 이 불완전한 타입은 GraphQL 경계 내부에만 존재하고 외부 계약은 `Document`다.
 */
type DocumentWirePayload = Omit<Document, 'bodyTiptap'>;

export type ActivityDetailPayloadByEntity = {
  PLAN: PlanDetailData;
  COMPLETION_REPORT: CompletionReport;
  POST_MORTEM: PostMortem;
  CO_ACTION: CoAction;
  CODE_REVIEW: CodeReview;
  DOCUMENT: Document;
  CONVENTION: Convention;
  SKILL: SkillPackage;
};

export type ActivityDetailMeta = {
  title: string | null;
  occurredAt: string | null;
  createdByName: string | null;
};

type ActivityDetailBase<TType extends HomeActivityEntityType, TPayload> = {
  __typename: string;
  id: string;
  projectId: string;
  entityType: TType;
  meta: ActivityDetailMeta;
  payload: TPayload;
};

export type ActivityDetailGraphqlResult =
  | ActivityDetailBase<'PLAN', PlanDetailData>
  | ActivityDetailBase<'COMPLETION_REPORT', CompletionReport>
  | ActivityDetailBase<'POST_MORTEM', PostMortem>
  | ActivityDetailBase<'CO_ACTION', CoAction>
  | ActivityDetailBase<'CODE_REVIEW', CodeReview>
  | ActivityDetailBase<'DOCUMENT', Document>
  | ActivityDetailBase<'CONVENTION', Convention>
  | ActivityDetailBase<'SKILL', SkillPackage>;

type ActivityDetailGraphqlWireResult =
  | Exclude<ActivityDetailGraphqlResult, ActivityDetailBase<'DOCUMENT', Document>>
  | ActivityDetailBase<'DOCUMENT', DocumentWirePayload>;

const ACTIVITY_DETAIL_QUERY = `
  query ActivityDetail($entityType: FeedEntityType!, $projectId: ID!, $id: ID!) {
    activityDetail(entityType: $entityType, projectId: $projectId, id: $id) {
      __typename
      ... on PlanActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on CompletionReportActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on PostMortemActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on CoActionActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on CodeReviewActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on DocumentActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on ConventionActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
      ... on SkillActivityDetail {
        id
        projectId
        entityType
        meta { title occurredAt createdByName }
        payload
      }
    }
  }
`;

type ActivityDetailGraphqlPayload = {
  data?: { activityDetail: ActivityDetailGraphqlWireResult | null } | null;
  errors?: Array<{ message: string }>;
};

/**
 * wire payload를 앱 계약(`Document`)으로 되돌린다. 변환은 브라우저 DOM이 필요하므로
 * queryFn 안(=이 함수)에서만 실행돼야 하며 SSR/prerender 시점으로 옮기면 안 된다.
 */
const hydrateDetail = async (detail: ActivityDetailGraphqlWireResult): Promise<ActivityDetailGraphqlResult> => {
  if (detail.entityType !== 'DOCUMENT') {
    return detail;
  }

  return {
    ...detail,
    payload: { ...detail.payload, bodyTiptap: await normalizeMarkdownToTiptap(detail.payload.body) },
  };
};

export const fetchActivityDetailGraphql = async (
  token: string,
  input: { entityType: HomeActivityEntityType; projectId: string; id: string },
  onRequestId?: (requestId: string | undefined) => void,
): Promise<ActivityDetailGraphqlResult> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({ query: ACTIVITY_DETAIL_QUERY, variables: input, operationName: 'ActivityDetail' }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch activity detail: ${response.status}`);
  }

  const requestId = readServerRequestId(response);
  onRequestId?.(requestId);
  const payload = (await response.json()) as ActivityDetailGraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw withServerRequestId(new Error(payload.errors[0]?.message ?? 'GraphQL activity detail failed'), requestId);
  }
  const detail = payload.data?.activityDetail;
  if (!detail) {
    throw withServerRequestId(new Error('GraphQL activity detail returned no data'), requestId);
  }
  return hydrateDetail(detail);
};
